import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  Eye,
  Users,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Info,
  Globe,
  LineChart,
  BarChart3,
  ExternalLink
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface TrafficData {
  trafficData: Array<{
    date: string;
    views: number;
    visitors: number;
  }>;
  summaryCards: {
    views: { value: number; change: number; trend: 'up' | 'down' };
    visitors: { value: number; change: number; trend: 'up' | 'down' };
    likes: { value: number; change: number; trend: 'up' | 'down' };
    comments: { value: number; change: number; trend: 'up' | 'down' };
  };
  locations: Array<{
    country: string;
    flag: string;
    views: number;
  }>;
  mostViewed: Array<{
    title: string;
    views: number;
    type: 'page' | 'package' | 'project';
  }>;
  referrers: Array<{
    source: string;
    views: number;
    type: 'search' | 'social' | 'direct' | 'external';
  }>;
}

const mockTrafficData: TrafficData = {
  trafficData: [
    { date: 'Aug 9', views: 35, visitors: 15 },
    { date: 'Aug 10', views: 10, visitors: 5 },
    { date: 'Aug 11', views: 55, visitors: 25 },
    { date: 'Aug 12', views: 75, visitors: 30 },
    { date: 'Aug 13', views: 45, visitors: 20 },
    { date: 'Aug 14', views: 45, visitors: 20 },
    { date: 'Aug 15', views: 15, visitors: 5 }
  ],
  summaryCards: {
    views: { value: 235, change: -48, trend: 'down' },
    visitors: { value: 122, change: -14, trend: 'down' },
    likes: { value: 0, change: 0, trend: 'up' },
    comments: { value: 0, change: 0, trend: 'up' }
  },
  locations: [
    { country: 'United States', flag: '🇺🇸', views: 70 },
    { country: 'India', flag: '🇮🇳', views: 56 },
    { country: 'Germany', flag: '🇩🇪', views: 29 },
    { country: 'United Kingdom', flag: '🇬🇧', views: 13 },
    { country: 'France', flag: '🇫🇷', views: 9 },
    { country: 'China', flag: '🇨🇳', views: 8 },
    { country: 'Taiwan', flag: '🇹🇼', views: 7 },
    { country: 'Australia', flag: '🇦🇺', views: 7 },
    { country: 'Singapore', flag: '🇸🇬', views: 4 },
    { country: 'Nigeria', flag: '🇳🇬', views: 4 }
  ],
  mostViewed: [
    { title: 'Home', views: 45, type: 'page' },
    { title: 'Tender Package - Construction Project A', views: 23, type: 'package' },
    { title: 'Project Documentation', views: 18, type: 'project' },
    { title: 'Contact Us', views: 14, type: 'page' },
    { title: 'Addendum Package - Infrastructure B', views: 7, type: 'package' },
    { title: 'About HG ACC', views: 5, type: 'page' },
    { title: 'Technical Specifications Package', views: 5, type: 'package' },
    { title: 'Project Timeline', views: 5, type: 'project' },
    { title: 'Quality Assurance Package', views: 4, type: 'package' },
    { title: 'Safety Guidelines', views: 4, type: 'project' }
  ],
  referrers: [
    { source: 'Search Engines', views: 61, type: 'search' },
    { source: 'perplexity.ai', views: 5, type: 'external' },
    { source: 'rts.pitchbook.com', views: 1, type: 'external' },
    { source: 'ecosia.org', views: 1, type: 'search' },
    { source: 'gemini.google.com', views: 1, type: 'search' },
    { source: 'Facebook', views: 1, type: 'social' }
  ]
};

const TrafficInsights: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'traffic' | 'insights'>('traffic');
  const [dateRange, setDateRange] = useState('Aug 9 - Aug 15, 2025');
  const [chartType, setChartType] = useState<'line' | 'bar'>('bar');
  const [showVisitors, setShowVisitors] = useState(true);
  const [timeUnit, setTimeUnit] = useState('Days');
  const [contentType, setContentType] = useState<'posts' | 'archive'>('posts');
  const [referrerType, setReferrerType] = useState('Search Engines');
  
  const data = mockTrafficData;

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-GB').format(num);
  };

  const getTrendIcon = (trend: 'up' | 'down') => {
    return trend === 'up' ? (
      <TrendingUp className="w-4 h-4 text-green-600" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-600" />
    );
  };

  const getTrendColor = (trend: 'up' | 'down') => {
    return trend === 'up' ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Traffic Insights Header */}
      <div className="border-b pb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Traffic Insights</h2>
        
        {/* Header with Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <div className="flex space-x-1">
              <button
                onClick={() => setActiveTab('traffic')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'traffic'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Traffic
              </button>
              <button
                onClick={() => setActiveTab('insights')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'insights'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Insights
              </button>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Last 7 Days</h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600">{dateRange}</span>
            <Button variant="outline" size="sm">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm">
              <Calendar className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Traffic Insights Section */}
      {activeTab === 'traffic' && (
        <>
          {/* Views Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold">Views</CardTitle>
                <div className="flex items-center space-x-4">
                  {/* Legend */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded"></div>
                      <span className="text-sm text-gray-600">Views</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-700 rounded"></div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={showVisitors}
                          onChange={(e) => setShowVisitors(e.target.checked)}
                          className="w-4 h-4 text-green-600"
                        />
                        <span className="text-sm text-gray-600">Visitors</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Time Unit Selector */}
                  <Select value={timeUnit} onValueChange={setTimeUnit}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Days">Days</SelectItem>
                      <SelectItem value="Weeks">Weeks</SelectItem>
                      <SelectItem value="Months">Months</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Chart Type Toggle */}
                  <div className="flex border rounded-md">
                    <button
                      onClick={() => setChartType('line')}
                      className={`px-3 py-1 text-sm ${
                        chartType === 'line'
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <LineChart className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setChartType('bar')}
                      className={`px-3 py-1 text-sm ${
                        chartType === 'bar'
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.trafficData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="views" fill="#4ade80" />
                    {showVisitors && <Bar dataKey="visitors" fill="#15803d" />}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Eye className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-gray-600">Views</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(data.summaryCards.views.value)}</p>
                    <div className="flex items-center mt-2">
                      {getTrendIcon(data.summaryCards.views.trend)}
                      <span className={`text-sm ml-1 ${getTrendColor(data.summaryCards.views.trend)}`}>
                        ↓ {Math.abs(data.summaryCards.views.change)}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-gray-600">Visitors</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(data.summaryCards.visitors.value)}</p>
                    <div className="flex items-center mt-2">
                      {getTrendIcon(data.summaryCards.visitors.trend)}
                      <span className={`text-sm ml-1 ${getTrendColor(data.summaryCards.visitors.trend)}`}>
                        ↓ {Math.abs(data.summaryCards.visitors.change)}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 text-yellow-600">★</div>
                      <span className="text-sm font-medium text-gray-600">Likes</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(data.summaryCards.likes.value)}</p>
                    <div className="flex items-center mt-2">
                      {getTrendIcon(data.summaryCards.likes.trend)}
                      <span className={`text-sm ml-1 ${getTrendColor(data.summaryCards.likes.trend)}`}>
                        {data.summaryCards.likes.change}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <div className="w-5 h-5 text-purple-600">💬</div>
                      <span className="text-sm font-medium text-gray-600">Comments</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{formatNumber(data.summaryCards.comments.value)}</p>
                    <div className="flex items-center mt-2">
                      {getTrendIcon(data.summaryCards.comments.trend)}
                      <span className={`text-sm ml-1 ${getTrendColor(data.summaryCards.comments.trend)}`}>
                        {data.summaryCards.comments.change}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Insights Section */}
      {activeTab === 'insights' && (
        <div className="space-y-6">
          {/* Locations and UTM/Clicks */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Locations */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <CardTitle>Locations</CardTitle>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* World Map Placeholder */}
                    <div className="bg-gray-100 rounded-lg p-4 h-64 flex items-center justify-center">
                      <div className="text-center">
                        <Globe className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">World Map Visualization</p>
                        <p className="text-sm text-gray-400">Interactive map showing user locations</p>
                      </div>
                    </div>
                    
                    {/* Countries List */}
                    <div>
                      <Tabs defaultValue="countries" className="w-full">
                        <TabsList className="grid w-full grid-cols-3">
                          <TabsTrigger value="countries">Countries</TabsTrigger>
                          <TabsTrigger value="regions">Regions</TabsTrigger>
                          <TabsTrigger value="cities">Cities</TabsTrigger>
                        </TabsList>
                        <TabsContent value="countries" className="mt-4">
                          <div className="space-y-3">
                            <h4 className="font-medium text-gray-900">Top countries</h4>
                            {data.locations.map((location, index) => (
                              <div key={location.country} className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <span className="text-lg">{location.flag}</span>
                                  <span className="text-sm font-medium">{location.country}</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-green-500 rounded-full"
                                      style={{ width: `${(location.views / 70) * 100}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-sm font-medium">{location.views}</span>
                                </div>
                              </div>
                            ))}
                            <div className="pt-2">
                              <a href="#" className="text-sm text-green-600 hover:text-green-700">View all</a>
                            </div>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* UTM and Clicks */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>UTM</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <LineChart className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      Your campaign UTM performance data will display here once...
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Clicks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <ExternalLink className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      Your most clicked external links will display here to track engaging content.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Most Viewed and Referrers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Most Viewed */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CardTitle>Most viewed</CardTitle>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setContentType('posts')}
                    className={`px-3 py-1 text-sm rounded ${
                      contentType === 'posts'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Posts & pages
                  </button>
                  <button
                    onClick={() => setContentType('archive')}
                    className={`px-3 py-1 text-sm rounded ${
                      contentType === 'archive'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Archive
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 text-sm font-medium text-gray-500 pb-2 border-b">
                    <span>Posts & pages</span>
                    <span className="text-right">Views</span>
                  </div>
                  {data.mostViewed.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${(item.views / 45) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium truncate">{item.title}</span>
                      </div>
                      <span className="text-sm font-medium ml-3">{item.views}</span>
                    </div>
                  ))}
                  <div className="pt-2">
                    <a href="#" className="text-sm text-green-600 hover:text-green-700">View all</a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referrers */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <CardTitle>Referrers</CardTitle>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <Select value={referrerType} onValueChange={setReferrerType}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Search Engines">Search Engines</SelectItem>
                      <SelectItem value="Social Media">Social Media</SelectItem>
                      <SelectItem value="Direct">Direct</SelectItem>
                      <SelectItem value="External">External</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 text-sm font-medium text-gray-500 pb-2 border-b">
                    <span>Referrers</span>
                    <span className="text-right">Views</span>
                  </div>
                  {data.referrers.map((referrer, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${(referrer.views / 61) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium truncate">{referrer.source}</span>
                      </div>
                      <span className="text-sm font-medium ml-3">{referrer.views}</span>
                    </div>
                  ))}
                  <div className="pt-2">
                    <a href="#" className="text-sm text-green-600 hover:text-green-700">View details</a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficInsights;
